'use strict';

/**
 * Batching transport. Events are queued in memory and flushed on a timer, on
 * batch-size, or immediately when the page is being unloaded (sendBeacon).
 * Failed flushes are re-queued and retried with backoff, and survive a reload
 * through localStorage so a network blip does not lose conversions.
 */
function createTransport(options) {
  const { endpoint, sdkKey, maxBatchSize, flushIntervalMs, storage, storageKey, getContext, onError } = options;

  let queue = storage.jsonGet(storageKey) || [];
  let timer = null;
  let failures = 0;
  let sending = false;
  let retryAt = 0;

  const persist = () => storage.jsonSet(storageKey, queue.slice(-200));

  function schedule() {
    if (timer || queue.length === 0) return;
    let backoff = failures > 0 ? Math.min(30000, flushIntervalMs * Math.pow(2, failures)) : flushIntervalMs;
    const throttled = retryAt - Date.now();
    if (throttled > backoff) backoff = Math.min(60000, throttled);
    timer = setTimeout(() => { timer = null; flush(); }, backoff);
  }

  // Timer and retry flushes have no caller context; without this the server
  // loses landingUrl/language/timezone/traits for most batches.
  function payload(batch, context) {
    let ctx = context;
    if (!ctx && typeof getContext === 'function') {
      try { ctx = getContext(); } catch (_) { ctx = null; }
    }
    return JSON.stringify({ sdkKey: sdkKey, events: batch, context: ctx || {} });
  }

  function beacon(body) {
    try {
      if (navigator.sendBeacon) {
        // text/plain avoids a CORS preflight during unload, when there is no time for one.
        return navigator.sendBeacon(endpoint, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
      }
    } catch (_) { /* fall through */ }
    return false;
  }

  function send(body, useKeepAlive) {
    const opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-sdk-key': sdkKey },
      body: body,
      credentials: 'omit',
      keepalive: Boolean(useKeepAlive),
      mode: 'cors',
    };
    // Cap wait so a hung attribution host cannot stall the page forever.
    if (typeof AbortController !== 'undefined' && !useKeepAlive) {
      const ctrl = new AbortController();
      opts.signal = ctrl.signal;
      // Hard abort so a hung collect host cannot keep browser sockets busy.
      const timer = setTimeout(function () { try { ctrl.abort(); } catch (_) { /* ignore */ } }, 1500);
      return fetch(endpoint, opts).finally(function () { clearTimeout(timer); });
    }
    return fetch(endpoint, opts);
  }

  function enqueue(event, context) {
    queue.push(event);
    persist();
    if (queue.length >= maxBatchSize) flush(context);
    else schedule();
  }

  function flush(context, immediate) {
    if (sending || queue.length === 0) return Promise.resolve(false);
    // Throttled: hold the batch (beacons included) until Retry-After passes.
    if (Date.now() < retryAt) { schedule(); return Promise.resolve(false); }
    if (timer) { clearTimeout(timer); timer = null; }

    const batch = queue.slice(0, maxBatchSize);
    const body = payload(batch, context);

    if (immediate && beacon(body)) {
      queue = queue.slice(batch.length);
      persist();
      return Promise.resolve(true);
    }

    sending = true;
    return send(body, immediate)
      .then((response) => {
        if (response.status === 429) {
          // Rate limited: keep the batch and retry after the server's hint.
          const header = response.headers && response.headers.get ? response.headers.get('retry-after') : null;
          const seconds = Number(header);
          retryAt = Date.now() + (Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 5000);
          throw new Error('rate limited');
        }
        if (!response.ok && response.status >= 500) throw new Error('server error ' + response.status);
        // 4xx means the payload will never be accepted - drop it rather than loop.
        queue = queue.slice(batch.length);
        failures = 0;
        persist();
        return true;
      })
      .catch((err) => {
        // Throttling is not a failure of the batch; never drop it for that.
        if (Date.now() >= retryAt) {
          failures += 1;
          if (failures > 6) { queue = queue.slice(batch.length); failures = 0; }
        }
        persist();
        if (onError) {
          try { onError(err); } catch (_) { /* host callbacks must never break the page */ }
        }
        return false;
      })
      .then((result) => { sending = false; schedule(); return result; });
  }

  return { enqueue: enqueue, flush: flush, size: () => queue.length };
}

module.exports = { createTransport };
